import { type ChangeEventHandler, useMemo } from "react";
import { useFormContext } from "react-hook-form";

import { ConsumablesFormStepSection } from "@jti/ui/components/ConsumablesFormStepSection";
import { RadioCard } from "@jti/ui/components/RadioCard";
import { StrengthIndicator } from "@jti/ui/components/StrengthIndicator";

import { useConsumablesStockStatuses } from "@product-details/forms/ConsumablesForm/hooks/useConsumablesStockStatuses";
import { useConsumablesProductDetailsContext } from "@product-details/providers";

import type { ConsumablesFormValues } from "../../ConsumablesForm.types";
import { getAvailableStrengths, getMatrixItemByProperties } from "../../utils";

import { ConsumablesStrengthSelectorSectionTitle } from "./ConsumablesStrengthSelectorSectionTitle";

const STRENGTH_SELECTOR_GROUP_NAME: keyof ConsumablesFormValues = "strengthSelector";

type ConsumablesStrengthSelectorSectionProps = {
  step: number;
  onChange?: ChangeEventHandler<HTMLInputElement>;
};

export const ConsumablesStrengthSelectorSection = ({ step, onChange }: ConsumablesStrengthSelectorSectionProps) => {
  const { register } = useFormContext<ConsumablesFormValues>();
  const { consumablesProductDetailsComponentData, currentMatrixItem } = useConsumablesProductDetailsContext();
  const { items: consumablesMatrixItems } = consumablesProductDetailsComponentData.consumablesMatrixData;

  const stockStatusesQueryState = useConsumablesStockStatuses();
  const elementsStockStatus = stockStatusesQueryState.data ?? {};

  const findMatrixItemForStrengthRating = (rating: number) =>
    getMatrixItemByProperties(
      consumablesMatrixItems,
      currentMatrixItem.packagingFormatId,
      currentMatrixItem.purchaseType,
      currentMatrixItem.flavorDetails?.flavour,
      rating
    );

  const visibleStrengths = useMemo(() => {
    const availableStrengths = getAvailableStrengths(consumablesMatrixItems, currentMatrixItem);

    if (stockStatusesQueryState.isLoading) {
      return [];
    }

    return availableStrengths.filter((strength) => {
      const matrixItem = findMatrixItemForStrengthRating(strength.rating);

      if (!matrixItem) return false;

      return elementsStockStatus[matrixItem.sku] !== "OUT_OF_STOCK";
    });
  }, [consumablesMatrixItems, currentMatrixItem, elementsStockStatus, stockStatusesQueryState.isLoading]);

  const registerProps = register(STRENGTH_SELECTOR_GROUP_NAME, { onChange });

  return (
    <ConsumablesFormStepSection
      title={<ConsumablesStrengthSelectorSectionTitle step={step} />}
      data-testid="strength-options"
    >
      {visibleStrengths.map((strength) => (
        <RadioCard
          key={strength.rating}
          groupName={STRENGTH_SELECTOR_GROUP_NAME}
          value={strength.rating}
          title={<StrengthIndicator name={strength.name} strength={strength.rating} />}
          {...registerProps}
        />
      ))}
    </ConsumablesFormStepSection>
  );
};
